/**
 * Auth Interceptor & Security Hardening Utility for Firebase SDK errors,
 * request rate-limiting, and input sanitization.
 */

import { signOut } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import { telemetry } from './telemetryLogger';

// Firebase error codes indicating expired or revoked sessions
const AUTH_ERROR_CODES = new Set([
  'permission-denied',
  'unauthenticated',
  'auth/id-token-expired',
  'auth/user-token-expired',
  'auth/invalid-user-token',
  'auth/user-disabled',
]);

/** Client-side rate limiting registry */
const requestTimestamps = new Map<string, number[]>();

/** Returns true if the error is a Firebase auth/permission error */
export const isAuthError = (error: any): boolean => {
  if (!error) return false;
  const code: string | undefined = error.code || error?.customData?.code;
  if (code && AUTH_ERROR_CODES.has(code)) return true;
  
  const msg: string = error.message || '';
  if (msg.includes('Missing or insufficient permissions') || msg.includes('PERMISSION_DENIED')) return true;
  return false;
};

let isRedirecting = false;

/**
 * Handles authorization failures across Firestore/Storage/Functions.
 */
export const handleAuthError = async (error: any): Promise<boolean> => {
  if (!isAuthError(error)) return false;

  if (isRedirecting) return true;
  isRedirecting = true;

  telemetry.log('WARNING', 'SECURITY_INTERCEPTOR', 'Auth error detected. Redirecting to login.', { code: error?.code, message: error?.message });

  try {
    await signOut(auth);
  } catch (_) {
    // Session already destroyed
  }

  window.location.hash = '#/login';
  window.location.reload();

  return true;
};

/**
 * Client-Side Rate Limiter Guard to protect sensitive mutation endpoints.
 * @param actionKey Identifier for the action (e.g. 'submit_application')
 * @param maxAllowed Maximum calls allowed within the window
 * @param windowMs Time window in milliseconds
 */
export const checkRateLimit = (actionKey: string, maxAllowed = 5, windowMs = 60000): boolean => {
  const now = Date.now();
  const timestamps = requestTimestamps.get(actionKey) || [];
  
  const validTimestamps = timestamps.filter((t) => now - t < windowMs);
  
  if (validTimestamps.length >= maxAllowed) {
    telemetry.log('WARNING', 'SECURITY_RATE_LIMIT', `Rate limit exceeded for action: ${actionKey}`);
    return false;
  }
  
  validTimestamps.push(now);
  requestTimestamps.set(actionKey, validTimestamps);
  return true;
};

/**
 * XSS & HTML Input Sanitizer helper.
 */
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .trim();
};
